import type { Interval, IntervalOrigin } from "@digits-graphql/frontend/graphql-bearer"
import dateTimeHelper from "@digits-shared/helpers/dateTimeHelper"
import dayjs from "@digits-shared/initializers/dayjs/dayjs"

/**
 * Accounting-specific All-Time Helpers
 *
 * These helpers require GraphQL types (Interval, IntervalOrigin) and dayjs.
 * Pure URL helpers live in @digits-shared/helpers/allTimeHelpers.
 */

/**
 * Computes the intervalCount for an all-time range.
 * This counts the number of intervals from the first transaction to today.
 */
export function computeAllTimeIntervalCount(
  interval: Interval,
  firstTransactionTimestamp: number
): number {
  const firstDate = dateTimeHelper.utcDayjsFromTimestamp(firstTransactionTimestamp)
  const today = dayjs().utc()
  const unit = dateTimeHelper.unitOfTimeForInterval(interval)
  const diff = today.startOf(unit).diff(firstDate.startOf(unit), unit)
  return Math.abs(diff) + 1
}

/**
 * Creates an all-time IntervalOrigin from the given interval and first transaction timestamp.
 * Returns undefined if no timestamp is available.
 */
export function createAllTimeOrigin(
  interval: Interval,
  firstTransactionTimestamp: number | undefined
): IntervalOrigin | undefined {
  if (!firstTransactionTimestamp) {
    return undefined
  }

  const todayOrigin = dateTimeHelper.todayIntervalOrigin(interval)
  const intervalCount = computeAllTimeIntervalCount(interval, firstTransactionTimestamp)

  return {
    ...todayOrigin,
    intervalCount,
  }
}

/**
 * Checks if the passed time params match the context origin.
 * This is used to determine if params are "explicit" (user selected)
 * or just the current context being passed through.
 */
export function paramsMatchOrigin(
  params: { year?: string | number; index?: string | number },
  origin: IntervalOrigin | undefined
): boolean {
  if (!origin || params.year === undefined) {
    return false
  }
  return (
    String(params.year) === String(origin.year) && String(params.index) === String(origin.index)
  )
}
